import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { fmtErr, inr, fmtDateTime } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";
import { toast } from "sonner";

export default function FranchiseCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/franchise/customers/${id}`).then(({ data }) => setData(data)).catch((e) => { toast.error(fmtErr(e)); setData({}); });
  }, [id]);

  if (!data) return <FullScreenLoader label="Loading customer" />;
  const c = data.customer || {};
  const b = data.business || {};

  return (
    <div className="space-y-5" data-testid="franchise-customer-detail">
      <Link to="/franchise/customers" data-testid="back-to-customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-shipany-blue hover:underline"><ArrowLeft className="h-4 w-4" /> All customers</Link>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-shipany-navy" data-testid="customer-detail-name">{c.name}</h1>
            <p className="text-sm text-slate-500">{c.email} · {c.phone}</p>
            <p className="mt-1 text-xs text-slate-400">{c.company || "—"} · {(c.franchise_meta?.business_type || "").replace(/_/g, " ")}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${c.kyc_status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="customer-detail-kyc">
            KYC {c.kyc_status}
          </span>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">Domestic Shipments</p><p className="mono text-lg font-bold text-shipany-navy">{b.domestic_shipments ?? 0}</p></div>
          <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">International</p><p className="mono text-lg font-bold text-shipany-navy">{b.international_shipments ?? 0}</p></div>
          <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">Total Business</p><p className="mono text-lg font-bold text-shipany-navy">{inr(b.total_business || 0)}</p></div>
          <div className="rounded-xl bg-emerald-50 p-4"><p className="text-xs font-semibold text-emerald-600">Your Earnings</p><p className="mono text-lg font-bold text-emerald-700" data-testid="customer-detail-earnings">{inr(b.franchise_earnings || 0)}</p></div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Shipments</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="py-2 pr-4">AWB</th><th className="py-2 pr-4">Courier</th><th className="py-2 pr-4">Destination</th><th className="py-2 pr-4 text-right">Price</th><th className="py-2 pr-4">Status</th><th className="py-2">Date</th></tr></thead>
            <tbody>
              {(data.shipments || []).map((s) => (
                <tr key={s.shipment_id} className="border-b border-slate-50" data-testid={`detail-shipment-${s.awb}`}>
                  <td className="mono py-2.5 pr-4 text-xs font-semibold text-shipany-navy">{s.awb || "—"}</td>
                  <td className="py-2.5 pr-4 text-xs">{s.courier_name}</td>
                  <td className="py-2.5 pr-4 text-xs">{s.destination_city}</td>
                  <td className="mono py-2.5 pr-4 text-right text-xs font-semibold">{inr(s.price || 0)}</td>
                  <td className="py-2.5 pr-4"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold">{s.status}</span></td>
                  <td className="py-2.5 text-xs text-slate-500">{fmtDateTime(s.created_at)}</td>
                </tr>
              ))}
              {!(data.shipments || []).length && <tr><td colSpan={6} className="py-6 text-center text-xs text-slate-400">No domestic shipments yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Commission History</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4 text-right">Invoice</th><th className="py-2 pr-4 text-right">Commission</th><th className="py-2">Status</th></tr></thead>
            <tbody>
              {(data.commissions || []).map((x) => (
                <tr key={x.commission_id} className="border-b border-slate-50" data-testid={`detail-commission-${x.commission_id}`}>
                  <td className="py-2.5 pr-4 text-xs text-slate-500">{fmtDateTime(x.created_at)}</td>
                  <td className="py-2.5 pr-4 text-xs capitalize">{x.commission_type}{x.weight_slab ? ` · ${x.weight_slab}` : ""}</td>
                  <td className="mono py-2.5 pr-4 text-right text-xs">{inr(x.invoice_amount_snapshot || 0)}</td>
                  <td className="mono py-2.5 pr-4 text-right text-xs font-bold text-emerald-600">{inr(x.commission_amount)}</td>
                  <td className="py-2.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${x.status === "credited" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{x.status}</span></td>
                </tr>
              ))}
              {!(data.commissions || []).length && <tr><td colSpan={5} className="py-6 text-center text-xs text-slate-400">No commissions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
